import * as L from 'leaflet';
import { HttpClient } from '@angular/common/http';
import { Legend } from '../controls/legendControl/legend';
import { TileLayer } from './tileLayer';
import { FeatureDisplayItemGenerator } from '../featureDisplay/featureDisplayItemGenerator';
import { WmsFeatureDisplayItemGenerator } from './wmsFeatureDisplayItemGenerator';
import 'jquery';

/*
export interface WmsTileLayerOptions {
  layers: string;
  format?: string;
  transparent?: boolean;
  version?: string;
  zIndex?: number;
  opacity?: number;
}*/

// options: http://leafletjs.com/reference-1.2.0.html#tilelayer-wms
export class WmsTileLayer extends TileLayer {
  protected getCapabilitiesXML: JQuery<XMLDocument>;
  protected getCapabilitiesPromise: null | Promise<JQuery<XMLDocument>>;

  constructor(id: string, name?: string) {
    super(id, name);
    // Default options
    this.options.setOptions({
      format: 'image/png',
      transparent: true,
    });

    this.setLegendCreatorFunction(this.createLegendsDefault.bind(this));
  }

  public getLeafletLayer(): Promise<null | L.Layer> {
    return new Promise((resolve) => {
      try {
        // use copy
        const options = {
          ...this.options.getAll(),
        };

        // make any option alterations that are required
        const optionsCopy = {};
        Object.keys(options).forEach((key: string) => {
          switch (true) {
            // we don't want bbx being set by anything other than client
            case key.toLowerCase() === 'bbox':
              break;
            default:
              optionsCopy[key] = options[key];
              break;
          }
        });
        return resolve(L.tileLayer.wms(this.url, optionsCopy));
        /* eslint-disable @typescript-eslint/no-unused-vars */
      } catch (e) {
        // TODO send user an error?
        console.error('Layer not found');
        return resolve(null);
      }
    });
  }

  public setUrl(url: string): this {
    // strip any query params off
    super.setUrl(url.replace(/\?.*$/, ''));
    return this;
  }

  public setFeatureIdentifiable(itemGenerator?: FeatureDisplayItemGenerator): this {
    this.setLayerClickFeatureItemGenerator(
      itemGenerator ? itemGenerator : new WmsFeatureDisplayItemGenerator(this),
    );
    return this;
  }

  // TODO: these static funcs could do with being re-written in a class of their own
  // Used for getting layer/style elements from xml and tries without workspace in name too
  public getElementWithName(
    $srcXml: JQuery<JQuery.Node>,
    name: string,
    parentElementType: string,
  ): null | JQuery<Element> {
    name = name.trim();
    // console.debug('layername', layerName);
    let $returnElement: null | JQuery<Element> = null;
    if (name.length > 0) {
      let $nameElement: JQuery<Element> = $srcXml
        .find(`${parentElementType}>Name:contains("${name}")`)
        .first();
      // if not found try without workspace (part before ":" in "europe:SHEEC_1000_1899")
      if ($nameElement.length === 0) {
        // get part after colon
        const newName = name.split(':').pop();
        if (name !== newName) {
          $nameElement = $srcXml.find(`${parentElementType}>Name:contains("${newName}")`).first();
        }
      }
      $returnElement = $nameElement.length > 0 ? $nameElement.parent() : null;
    }
    return $returnElement;
  }

  /**
   * @returns promise of jQuery object of xml response
   */
  public refreshGetCapabilitiesXml(
    http: HttpClient,
    additionalParams = new Map<string, string>(),
  ): Promise<JQuery<XMLDocument>> {
    // defaults to 1.1.1 as leaflet appears to.
    const version: string = this.options.get('version') ?? '1.1.1';

    // set defaults
    const params = new Map<string, string>([
      ['service', 'WMS'],
      ['request', 'GetCapabilities'],
      ['version', version],
    ]);
    // set any additional values (allows overriding)
    additionalParams.forEach((value: string, key: string) => {
      params.set(key, value);
    });

    const url =
      this.url +
      '?' +
      Array.from(params.keys())
        .filter((key) => null != params.get(key))
        .map((key) => `${key}=${params.get(key)}`)
        .join('&');

    this.getCapabilitiesPromise = http
      .get(url, { responseType: 'text' })
      .toPromise()
      .catch(() => '')
      .then((res: string) => {
        const xml = $.parseXML(res);
        this.getCapabilitiesXML = jQuery(xml);
        return this.getCapabilitiesXML;
      })
      .finally(() => {
        this.getCapabilitiesPromise = null;
      });
    return this.getCapabilitiesPromise;
  }

  public getCapabilitiesXml(http: HttpClient): Promise<JQuery<XMLDocument>> {
    switch (true) {
      case null != this.getCapabilitiesXML:
        return Promise.resolve(this.getCapabilitiesXML);
      case null != this.getCapabilitiesPromise:
        return this.getCapabilitiesPromise!;
      default:
        return this.refreshGetCapabilitiesXml(http);
    }
  }

  public createLegendsDefault(
    layer: WmsTileLayer,
    http: HttpClient,
  ): Promise<null | Array<Legend>> {
    const layerNames =
      layer.options.get('layers') != null ? layer.options.get<string>('layers')!.split(',') : [];
    const styleNames =
      layer.options.get('styles') != null ? layer.options.get<string>('styles')!.split(',') : [];

    return Promise.resolve<null | Array<Legend>>(
      this.getCapabilitiesXml(http)
        .then(($xml: JQuery<XMLDocument>) => {
          const legends = new Array<Legend>();

          layerNames.forEach((layerName: string) => {
            const $layerElement = this.getElementWithName($xml, layerName, 'Layer');

            if (null != $layerElement) {
              // console.debug('layerElement', $layerElement);
              // console.debug('styleName', styleNames);
              const $styleElements = new Array<JQuery<Element>>();
              styleNames.forEach((styleName: string) => {
                const $styleElement = this.getElementWithName($layerElement, styleName, 'Style');
                if (null != $styleElement) {
                  $styleElements.push($styleElement);
                }
              });
              // if no matching style elements just get the first one
              if ($styleElements.length === 0) {
                $styleElements.push($layerElement.find('Style').first());
              }
              // console.debug('styleElement', $styleElements);
              $styleElements.forEach(($styleElement) => {
                const $titleElement = $styleElement.find('Title').first();
                const $onlineResourceElement = $styleElement.find('OnlineResource').first();

                if ($onlineResourceElement.length > 0) {
                  const href = $onlineResourceElement.attr('xlink:href');
                  if (href != null && href.trim().length > 0) {
                    const name =
                      $titleElement.length === 0
                        ? layer.name
                        : `<span>${
                            layer.name
                          }</span><span class="multilayer-specific-name">${String(
                            $titleElement.html(),
                          )}</span>`;
                    legends.push(
                      new Legend(layer.id, name).setDisplayFunction(() => {
                        const img = document.createElement('img');
                        img.setAttribute('src', href);
                        return img;
                      }),
                    );
                  }
                }
              });
            }
          });
          return legends;
        })
        .catch((message) => {
          console.log('No WMS legend found', message);
          return null;
        }),
    );
  }
}
